// TODO: To be deleted when the FF for the smart panel is cleaned-up.

import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { tokens, ToggleGroup, Text, Tooltip, Loader, Icon } from '@hotjar/hotsauce';
import type { IconSetDefinitions } from 'packages/hotsauce/build/components/Icon/icons';
import { abbrNumber } from '@hotjar/insights-common/lib/number';
import { plural } from '@hotjar/insights-common/lib/plural';
import { HEATMAP_DATA_TYPES, HEATMAP_DEVICE_TYPES } from '@hotjar/heatmaps';
import { useGetDeviceSessions } from '@hotjar/webapp/domains/heatmaps/hooks/useGetDeviceSessions';
import type { ModelState } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/Report/ReportTypes';

import { WIDGET_Z_INDEX } from './constants';

const { DESKTOP, TABLET, PHONE } = HEATMAP_DEVICE_TYPES;

const ToggleGroupContainer = styled.div`
  pointer-events: auto;
  margin-top: auto;
  display: flex;
  flex-direction: row;
  position: fixed;
  bottom: ${tokens.spacer.x8};
  right: ${tokens.spacer.x7};
  z-index: ${WIDGET_Z_INDEX};
  gap: ${tokens.spacer.x1};
`;

const CountContainer = styled.div`
  background: ${tokens.color.ui_background};
  border: 1px solid ${tokens.color.ui_border};
  height: ${tokens.spacer.x5};
  padding: ${tokens.spacer.x1pt5};
  display: flex;
  align-items: center;
  border-radius: 6px;
`;

interface OptionsWidgetProps {
  reportState: ModelState;
  clickCount?: number;
  selectedDeviceType: HeatmapDeviceTypes;
  selectedHeatType: HeatmapDataType;
  onDeviceTypeChange: (type: HeatmapDeviceTypes) => void;
  onHeatTypeChange: (type: HeatmapDataType) => void;
}

const DeviceToggleItem = ({
  device,
  label,
  iconName,
  sessionsCount,
  isFetching,
  isError,
}: {
  device: typeof DESKTOP | typeof TABLET | typeof PHONE;
  label: string;
  iconName: keyof IconSetDefinitions;
  sessionsCount: number;
  isFetching: boolean;
  isError: boolean;
}) => {
  let tooltipText = `${abbrNumber(sessionsCount)} ${device} ${plural(
    { one: 'session', other: 'sessions' },
    sessionsCount,
  )}`;
  if (isError) tooltipText = 'Number of sessions unavailable';

  return (
    <Tooltip isDisabled={isFetching} text={tooltipText}>
      <span
        aria-label={label}
        css={css`
          display: flex;
        `}
      >
        <Icon
          icon={iconName}
          size="m"
          css={css`
            margin-right: ${tokens.spacer.x1};
          `}
        />
        {isFetching ? (
          <Loader size="m" />
        ) : (
          <Text as="span" textType="caption">{`${isError ? '-' : abbrNumber(sessionsCount)}`}</Text>
        )}
      </span>
    </Tooltip>
  );
};

const ClickCount = ({
  device,
  state,
  value,
}: {
  device: HeatmapDeviceTypes;
  state: ModelState;
  value?: number;
}): JSX.Element => {
  const clicksOrTaps =
    device === 'desktop'
      ? plural({ one: 'click', other: 'clicks' }, value)
      : plural({ one: 'tap', other: 'taps' }, value);

  let countText = `${abbrNumber(value)} ${clicksOrTaps}`;
  let tooltipText = `Total ${device} ${clicksOrTaps}`;

  if (state === 'FETCH_ERROR' || value === undefined) {
    countText = '- ';
    tooltipText = `Number of ${clicksOrTaps} unavailable`;
  }

  return (
    <CountContainer>
      <Tooltip text={tooltipText}>
        <span
          aria-label="Total count"
          css={css`
            display: flex;
          `}
        >
          <Icon
            icon={device === 'desktop' ? 'cursor_click' : 'finger_tap'}
            size="m"
            css={css`
              margin-right: ${tokens.spacer.x1};
            `}
          />
          <Text as="span" textType="caption">
            {state === 'FETCHING' ? <Loader size="m" /> : countText}
          </Text>
        </span>
      </Tooltip>
    </CountContainer>
  );
};

export function OptionsWidget({
  reportState,
  clickCount,
  selectedDeviceType,
  selectedHeatType,
  onDeviceTypeChange,
  onHeatTypeChange,
}: OptionsWidgetProps): JSX.Element {
  const { isFetching, sessions, isError } = useGetDeviceSessions();

  return (
    <ToggleGroupContainer>
      {selectedHeatType === 'click' && (
        <ClickCount value={clickCount} device={selectedDeviceType} state={reportState} />
      )}

      <ToggleGroup
        description="Device type"
        orientation="horizontal"
        onChange={(selectedType) => onDeviceTypeChange(selectedType as HeatmapDeviceTypes)}
        selectedValue={selectedDeviceType}
      >
        <ToggleGroup.Toggle value={DESKTOP} key={DESKTOP}>
          {/* @ts-expect-error until DS supports an Element, not just a string */}
          <DeviceToggleItem
            device={DESKTOP}
            label="Desktop"
            iconName="device_desktop"
            sessionsCount={sessions[DESKTOP]}
            isFetching={isFetching}
            isError={isError}
          />
        </ToggleGroup.Toggle>

        <ToggleGroup.Toggle value={TABLET} key={TABLET}>
          {/* @ts-expect-error until DS supports an Element, not just a string */}
          <DeviceToggleItem
            device={TABLET}
            label="Tablet"
            iconName="device_tablet"
            sessionsCount={sessions[TABLET]}
            isFetching={isFetching}
            isError={isError}
          />
        </ToggleGroup.Toggle>
        <ToggleGroup.Toggle value={PHONE} key={PHONE}>
          {/* @ts-expect-error until DS supports an Element, not just a string */}
          <DeviceToggleItem
            device={PHONE}
            label="Phone"
            iconName="device_phone"
            sessionsCount={sessions[PHONE]}
            isFetching={isFetching}
            isError={isError}
          />
        </ToggleGroup.Toggle>
      </ToggleGroup>

      {selectedDeviceType === DESKTOP ? (
        <ToggleGroup
          description="Heat type"
          orientation="horizontal"
          onChange={(selectedType) => onHeatTypeChange(selectedType as HeatmapDataType)}
          selectedValue={selectedHeatType}
        >
          <ToggleGroup.IconToggle
            value={HEATMAP_DATA_TYPES.CLICK}
            icon="cursor_click"
            tooltipText="Click"
            description="Click"
          />
          <ToggleGroup.IconToggle
            value={HEATMAP_DATA_TYPES.MOVEMENT}
            icon="cursor_move"
            tooltipText="Move"
            description="Move"
          />
          <ToggleGroup.IconToggle
            value={HEATMAP_DATA_TYPES.SCROLL}
            icon="scroll"
            tooltipText="Scroll"
            description="Scroll"
          />
        </ToggleGroup>
      ) : (
        <ToggleGroup
          description="Heat type"
          orientation="horizontal"
          onChange={(selectedType) => onHeatTypeChange(selectedType as HeatmapDataType)}
          selectedValue={selectedHeatType}
        >
          <ToggleGroup.IconToggle
            value={HEATMAP_DATA_TYPES.CLICK}
            icon="finger_tap"
            tooltipText="Tap"
            description="Tap"
          />
          <ToggleGroup.IconToggle
            value={HEATMAP_DATA_TYPES.SCROLL}
            icon="scroll"
            tooltipText="Scroll"
            description="Scroll"
          />
        </ToggleGroup>
      )}
    </ToggleGroupContainer>
  );
}
